import { computed, type Ref } from 'vue';

import type { useUserStore } from '@/stores/user';

import type {
  SlideOverCampaignCriteriaItem,
  SlideOverSelectOption,
} from '../types';

type UserStore = ReturnType<typeof useUserStore>;

export function useSlideOverCampaignCriteria(params: {
  campaignId: Ref<number | null | undefined>;
  userStore: UserStore;
}) {
  const campaign = computed(() =>
    params.userStore.allCampaigns?.find(
      (item) => item.id === params.campaignId.value
    )
  );

  const campaignCriteria = computed<
    SlideOverCampaignCriteriaItem[] | undefined
  >(() =>
    campaign.value?.criteria?.map((criteria) => ({
      criteriaId: criteria.id,
      weight: criteria.weight,
      criteria: {
        id: criteria.id,
        labelHelper: criteria.labelHelper,
        type: criteria.type,
        options: criteria.options,
      },
    }))
  );

  const getCriteriaOptions = (criteriaId: number): SlideOverSelectOption[] => {
    const criteria = campaign.value?.criteria?.find(
      (item) => item.id === criteriaId
    );

    if (!criteria) {
      return [];
    }

    return criteria.options.map((option) => ({
      label: option.value,
      value: option.key,
    }));
  };

  const buildInitialReactivationCriteria = (): Record<
    string,
    string | string[]
  > => {
    const criteriaItems = campaignCriteria.value;
    const userCriteria = params.userStore.user?.userCriteria;

    if (!criteriaItems?.length) {
      return {};
    }

    const criteriaObject: Record<string, string | string[]> = {};

    // Initialize all campaign criteria with empty defaults.
    criteriaItems.forEach((item) => {
      const isMultiSelect = item.criteria.type === 'multiSelect';
      criteriaObject[String(item.criteriaId)] = isMultiSelect ? [] : '';
    });

    // Prefill with the helper's existing answers when available.
    if (userCriteria?.length) {
      userCriteria.forEach((item) => {
        const key = String(item.criteriaId);
        if (!(key in criteriaObject)) {
          return;
        }

        if (item.answerKeys?.length) {
          criteriaObject[key] = item.answerKeys;
          return;
        }

        if (item.answerKey) {
          criteriaObject[key] = item.answerKey;
        }
      });
    }

    return criteriaObject;
  };

  return {
    campaign,
    campaignCriteria,
    getCriteriaOptions,
    buildInitialReactivationCriteria,
  };
}
